import Button from "components/buttons/Button/Button";
import type React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import SidebarBusyOverlay from "~/components/Sidebar/components/SidebarBusyOverlay";
import SidebarContent from "~/components/Sidebar/components/SidebarContent";
import SidebarFooter from "~/components/Sidebar/components/SidebarFooter";
import SidebarHeader from "~/components/Sidebar/components/SidebarHeader";
import SidebarHeaderHeadline from "~/components/Sidebar/components/SidebarHeaderHeadline";
import Sidebar from "~/components/Sidebar/Sidebar";
import type { Location, UserAvailableLocationType } from "~/modules/location/api/location/locationTypes.ts";
import { useStaffing } from "~/modules/project/api/staffing/staffingQueries.ts";
import type { UsersActiveStaffingType } from "~/modules/project/api/staffing/staffingTypes.ts";
import type { Quota } from "~/modules/quota/api/quota/quotaTypes.ts";
import type { UserAvailableDeliverableType } from "~/modules/timeTracking/api/deliverable/deliverableTypes.ts";
import type {
	TimeTrackingFormData,
	TimeTrackingWithQuotaLock,
} from "~/modules/timeTracking/api/timeTracking/timeTrackingTypes.ts";
import type { TimeTrackingType } from "~/modules/timeTracking/api/timeTrackingType/timeTrackingTypeTypes.ts";
import { TimeTrackingTypeId } from "~/modules/timeTracking/api/timeTrackingType/timeTrackingTypeTypes.ts";
import Projects from "~/modules/timeTracking/components/components/Projects";
import CreateOrUpdateTimeTrackingForm from "~/modules/timeTracking/components/forms/CreateTimeTrackingForm/CreateOrUpdateTimeTrackingForm";
import {
	createTimeTrackingFormDefaultValues,
} from "~/modules/timeTracking/components/forms/CreateTimeTrackingForm/createTimeTrackingFormDefaultValues.ts";
import SyncUserAssetsForm from "~/modules/timeTracking/components/forms/SyncUserAssetsForm";
import { TimeTrackingFormNamesEnum } from "~/modules/timeTracking/types/timeTrackingTypes.ts";
import { getTimeTrackingTypeDisplayNameById } from "~/modules/timeTracking/utils/timeTrackingTypeUtils.ts";
import {
	getAvailableUserDeliverables,
	getAvailableUserLocations,
	getStaffingIdsOutsidePeriod,
	getStaffingTimeTrackingSummaryFromActiveStaffings, getStaffingTimeTrackingSummaryFromStaffing,
} from "~/modules/timeTracking/utils/timeTrackingUtils.ts";
import type { User } from "~/modules/user/api/user/userTypes.ts";
import { normalizeTKey } from "~/types/typeHelpers.ts";
import { getHoursAndMinutesFromMinutes } from "~/utils/dateAndTimeUtils.ts";

type UpdateTimeTrackingSidebarProps = {
	activeStaffings: UsersActiveStaffingType[];
	userDeliverables: UserAvailableDeliverableType[];
	userLocations: UserAvailableLocationType[];
	close: () => void;
	isOpen: boolean;
	date: null | Date,
	locations: Location[];
	quotas: Record<string, Quota>;
	timeTrackingData: TimeTrackingWithQuotaLock | null;
	selectedUserId: User["id"];
	timeTrackingTypes: TimeTrackingType[];
	users: User[];
};

const UpdateTimeTrackingSidebar: React.FunctionComponent<UpdateTimeTrackingSidebarProps> = ({
	activeStaffings,
	userDeliverables,
	userLocations,
	close,
	isOpen,
	date,
	locations,
	quotas,
	selectedUserId,
	timeTrackingData,
	users,
}) => {
	const { t } = useTranslation();

	const [selectedTimeTrackingTypeId, setSelectedTimeTrackingTypeId] = useState<TimeTrackingTypeId>(TimeTrackingTypeId.Project);
	const [selectedStaffingId, setSelectedStaffingId] = useState<string | null>(null);
	const [currentFormName, setCurrentFormName] = useState<TimeTrackingFormNamesEnum | null>(TimeTrackingFormNamesEnum.TIME_TRACKING_FORM);
	const [currentTimeTrackingFormValues, setCurrentTimeTrackingFormValues] = useState<TimeTrackingFormData>(createTimeTrackingFormDefaultValues);

	useEffect(() => {
		if (timeTrackingData) {
			const { hours, minutes } = getHoursAndMinutesFromMinutes(timeTrackingData.minutes);

			const formData = {
				hours,
				minutes,
				locationName: timeTrackingData.locationName,
				text: timeTrackingData.text || "",
				isBillable: timeTrackingData.isBillable,
				bookOnQuota: timeTrackingData.quotaId !== null,
			};
			setSelectedStaffingId(timeTrackingData.staffingId || null);
			setSelectedTimeTrackingTypeId(timeTrackingData.timeTrackingTypeId);
			setCurrentTimeTrackingFormValues(formData);
		}
	}, [timeTrackingData]);

	let selectedStaffingData = useMemo(() => {
		if (selectedStaffingId && activeStaffings) {
			return getStaffingTimeTrackingSummaryFromActiveStaffings(selectedStaffingId, activeStaffings);
		}

		return null;
	}, [selectedStaffingId, activeStaffings]);

	// The stored staffing stays selectable: a tracking that keeps its staffing is not refused.
	const staffingIdsOutsidePeriod = useMemo(() => {
		return getStaffingIdsOutsidePeriod({
			date,
			exceptStaffingId: timeTrackingData?.staffingId,
			projects: activeStaffings,
		});
	}, [activeStaffings, date, timeTrackingData?.staffingId]);

	const selectedStaffingProjectId = selectedTimeTrackingTypeId === TimeTrackingTypeId.Project ? selectedStaffingData?.projectId : null;

	const closeAndReset = useCallback(() => {
		close();
		// reset state after closing animation ends
		setTimeout(() => {
			setCurrentTimeTrackingFormValues(createTimeTrackingFormDefaultValues);
			setSelectedTimeTrackingTypeId(TimeTrackingTypeId.Project);
			setSelectedStaffingId(null);
			setCurrentFormName(TimeTrackingFormNamesEnum.TIME_TRACKING_FORM);
		}, 800);
	}, [close]);

	const handleStaffingIdClick = useCallback((value: string) => {
		setSelectedStaffingId(value);
		setCurrentFormName(TimeTrackingFormNamesEnum.TIME_TRACKING_FORM);
	}, []);


	const selectedTimeTrackingTypeName = getTimeTrackingTypeDisplayNameById(selectedTimeTrackingTypeId);

	const currentQuotaBooking = useMemo(() => {
		if (!timeTrackingData?.quotaId || !timeTrackingData.staffingId) {
			return null;
		}

		return {
			minutes: timeTrackingData.minutes,
			quotaId: timeTrackingData.quotaId,
			staffingId: timeTrackingData.staffingId,
		};
	}, [timeTrackingData]);

	const availableLocations = useMemo(() => {
		return getAvailableUserLocations({
			userLocations,
			selectedStaffingProjectId,
			selectedTimeTrackingTypeId,
		});
	}, [userLocations, selectedStaffingProjectId, selectedTimeTrackingTypeId]);

	const availableDeliverables = useMemo(() => {
		return getAvailableUserDeliverables({
			userDeliverables,
			selectedStaffingProjectId,
			selectedTimeTrackingTypeId,
		});
	}, [userDeliverables, selectedStaffingProjectId, selectedTimeTrackingTypeId]);

	const loadMissingStaffingData = isOpen && currentFormName === TimeTrackingFormNamesEnum.TIME_TRACKING_FORM && selectedTimeTrackingTypeId === TimeTrackingTypeId.Project && !selectedStaffingData;

	const {data:staffingData, isLoading: isLoadingStaffingData} = useStaffing({projectId:timeTrackingData?.projectId || "", staffingId:timeTrackingData?.staffingId || "", enabled: loadMissingStaffingData});

	const showLoader = loadMissingStaffingData && isLoadingStaffingData;

	selectedStaffingData =loadMissingStaffingData && staffingData ? getStaffingTimeTrackingSummaryFromStaffing(staffingData) : selectedStaffingData;
	return (
		<Sidebar closeOnOutsideClick={false}
				 open={isOpen}
				 setOpen={closeAndReset}>
			<div className="flex min-h-full w-full flex-col justify-start">
				{currentFormName === null &&
					<>
						<SidebarHeader>
							<SidebarHeaderHeadline>Projekt / Bestellung wählen</SidebarHeaderHeadline>
						</SidebarHeader>
						<SidebarContent>
							<Projects
								usersActiveStaffings={activeStaffings}
								disabledStaffingIds={[]}
								staffingIdsOutsidePeriod={staffingIdsOutsidePeriod}
								value={selectedStaffingId}
								onChange={handleStaffingIdClick}
							/>
						</SidebarContent>
						<SidebarFooter>
							<Button theme="none"
									onClick={closeAndReset}>
								{t(normalizeTKey("form:buttonTexts.cancel"))}
							</Button>
						</SidebarFooter>
					</>}
				{currentFormName === TimeTrackingFormNamesEnum.TIME_TRACKING_FORM && showLoader && <SidebarBusyOverlay/>}
				{currentFormName === TimeTrackingFormNamesEnum.TIME_TRACKING_FORM && !showLoader &&
					<CreateOrUpdateTimeTrackingForm
						availableDeliverables={availableDeliverables}
						availableLocations={availableLocations}
						currentQuotaBooking={currentQuotaBooking}
						date={date}
						parentIsVisible={isOpen}
						quotaLock={timeTrackingData?.quotaLock ?? null}
						initialValues={currentTimeTrackingFormValues}
						locations={locations}
						quotas={quotas}
						onSuccess={closeAndReset}
						setCurrentTimeTrackingFormValues={setCurrentTimeTrackingFormValues}
						selectedStaffingData={selectedStaffingData}
						selectedStaffingId={selectedStaffingId}
						selectedTimeTrackingTypeId={selectedTimeTrackingTypeId}
						selectedTimeTrackingTypeName={selectedTimeTrackingTypeName}
						selectedUserId={selectedUserId}
						setCurrentFormName={setCurrentFormName}
						timeTrackingId={timeTrackingData?.id}
						users={users} />}
				{currentFormName === TimeTrackingFormNamesEnum.LOCATION_FORM &&
					<SyncUserAssetsForm
						assetType="location"
						existingAssets={availableLocations}
						userId={selectedUserId}
						projectId={selectedStaffingProjectId}
						projectTitle={selectedStaffingData?.projectTitle}
						setCurrentFormName={setCurrentFormName}
						timeTrackingTypeId={selectedTimeTrackingTypeId} />}
				{currentFormName === TimeTrackingFormNamesEnum.DELIVERABLE_FORM &&
					<SyncUserAssetsForm
						assetType="deliverable"
						existingAssets={availableDeliverables}
						userId={selectedUserId}
						projectId={selectedStaffingProjectId}
						projectTitle={selectedStaffingData?.projectTitle}
						setCurrentFormName={setCurrentFormName}
						timeTrackingTypeId={selectedTimeTrackingTypeId} />}
			</div>
		</Sidebar>
	);
};

export default UpdateTimeTrackingSidebar;
